import React, { useEffect, useRef, useState } from "react";
import logoF from "../img/logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./Navbar.css";

const Navbar = () => {
  const [busqueda, setBusqueda] = useState("");
  const [mostrarModal, setMostrarModal] = useState(false);
  const [alerta, setAlerta] = useState("");
  const [resultados, setResultados] = useState([]);
  const [productos, setProductos] = useState([]);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [catalogoAbierto, setCatalogoAbierto] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const debounceTimeout = useRef(null);
  const navbarRef = useRef(null);

  // =========================================================
  // CARGAR PRODUCTOS
  // =========================================================
  useEffect(() => {
    const cargarProductos = async () => {
      try {
        const res = await axios.get(
          "https://gj-frio-industrial-api.onrender.com",
        );

        const todos = Object.values(res.data).flat();

        setProductos(todos);
      } catch (error) {
        console.error("Error cargando productos:", error);
      }
    };

    cargarProductos();
  }, []);

  // =========================================================
  // ELIMINAR TILDES Y NORMALIZAR TEXTO
  // =========================================================
  const eliminarTildes = (texto = "") =>
    texto
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  // =========================================================
  // BUSCADOR
  // =========================================================
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (busqueda.trim() === "") {
        setResultados([]);
        setAlerta("");
        return;
      }

      const busquedaNorm = eliminarTildes(busqueda);

      const coincidencias = productos.filter((producto) =>
        eliminarTildes(producto.nombre).includes(busquedaNorm),
      );

      setResultados(coincidencias.slice(0, 6));

      setAlerta(
        /\b(papa|solo|este|aun)\b/i.test(busqueda)
          ? "Algunas palabras podrían llevar tilde."
          : "",
      );
    }, 150);

    return () => {
      clearTimeout(debounceTimeout.current);
    };
  }, [busqueda, productos]);

  // =========================================================
  // REALIZAR BÚSQUEDA
  // =========================================================
  const manejarBusqueda = () => {
    const texto = busqueda.trim();

    if (!texto) return;

    navigate(`/resultados?busqueda=${encodeURIComponent(texto)}`);

    cerrarBuscador();
    cerrarMenu();
  };

  const manejarEnter = (e) => {
    if (e.key === "Enter") {
      manejarBusqueda();
    }
  };

  // =========================================================
  // CERRAR BUSCADOR
  // =========================================================
  const cerrarBuscador = () => {
    setMostrarModal(false);
    setResultados([]);
    setBusqueda("");
    setAlerta("");
  };

  // =========================================================
  // CERRAR MENÚ MOBILE
  // =========================================================
  const cerrarMenu = () => {
    setMenuAbierto(false);
    setCatalogoAbierto(false);
  };

  // =========================================================
  // NAVEGAR
  // =========================================================
  const irA = (ruta) => {
    navigate(ruta);
    cerrarMenu();
  };

  // =========================================================
  // SABER SI UNA RUTA ESTÁ ACTIVA
  // =========================================================
  const rutaActiva = (ruta) => {
    if (ruta === "/") {
      return location.pathname === "/";
    }

    return location.pathname.startsWith(ruta);
  };

  const catalogoActivo =
    rutaActiva("/industrialr") ||
    rutaActiva("/nevera") ||
    rutaActiva("/lavadora") ||
    rutaActiva("/aireacondicionado") ||
    rutaActiva("/gas") ||
    rutaActiva("/herramienta");

  // =========================================================
  // BLOQUEAR SCROLL CUANDO EL BUSCADOR ESTÁ ABIERTO
  // =========================================================
  useEffect(() => {
    if (mostrarModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [mostrarModal]);

  return (
    <>
      <header className="gj-navbar" ref={navbarRef}>
        <div className="gj-navbar-container">
          {/* =====================================================
              LOGO
          ===================================================== */}
          <button
            type="button"
            className="gj-logo"
            onClick={() => irA("/")}
            aria-label="Ir al inicio"
          >
            <img src={logoF} alt="G&J Frío Industrial y Doméstico" />

            <div className="gj-logo-text">
              <span className="gj-logo-title">G&J FRÍO</span>
              <span className="gj-logo-subtitle">Industrial y Doméstico</span>
            </div>
          </button>

          {/* =====================================================
              BOTÓN MOBILE
          ===================================================== */}
          <button
            type="button"
            className={`gj-menu-toggle ${menuAbierto ? "active" : ""}`}
            onClick={() => setMenuAbierto((prev) => !prev)}
            aria-label="Abrir menú"
            aria-expanded={menuAbierto}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          {/* =====================================================
              MENÚ
          ===================================================== */}
          <div
            className={`gj-navbar-collapse ${
              menuAbierto ? "gj-navbar-collapse-open" : ""
            }`}
          >
            <nav className="gj-nav">
              {/* INICIO */}
              <button
                type="button"
                className={`gj-nav-link ${rutaActiva("/") ? "active" : ""}`}
                onClick={() => irA("/")}
              >
                <i className="bi bi-house-door"></i>
                <span>Inicio</span>
              </button>

              {/* NOSOTROS */}
              <button
                type="button"
                className={`gj-nav-link ${
                  rutaActiva("/quienes") ? "active" : ""
                }`}
                onClick={() => irA("/quienes")}
              >
                <i className="bi bi-people"></i>
                <span>Nosotros</span>
              </button>

              {/* CONTACTO */}
              <button
                type="button"
                className={`gj-nav-link ${
                  rutaActiva("/contacto") ? "active" : ""
                }`}
                onClick={() => irA("/contacto")}
              >
                <i className="bi bi-telephone"></i>
                <span>Contacto</span>
              </button>

              {/* =================================================
                  CATÁLOGO
              ================================================= */}
              <div
                className={`gj-dropdown ${
                  catalogoAbierto ? "gj-dropdown-open" : ""
                }`}
                onMouseEnter={() => {
                  if (window.innerWidth >= 992) {
                    setCatalogoAbierto(true);
                  }
                }}
                onMouseLeave={() => {
                  if (window.innerWidth >= 992) {
                    setCatalogoAbierto(false);
                  }
                }}
              >
                <button
                  type="button"
                  className={`gj-nav-link gj-dropdown-toggle ${
                    catalogoActivo ? "active" : ""
                  }`}
                  onClick={() => setCatalogoAbierto((prev) => !prev)}
                >
                  <i className="bi bi-grid"></i>

                  <span>Catálogo</span>

                  <i
                    className={`bi bi-chevron-down gj-dropdown-arrow ${
                      catalogoAbierto ? "rotate" : ""
                    }`}
                  ></i>
                </button>

                <div className="gj-dropdown-menu">
                  <div className="gj-dropdown-header">
                    <span className="gj-dropdown-header-icon">
                      <i className="bi bi-box-seam"></i>
                    </span>

                    <div>
                      <strong>Nuestro catálogo</strong>
                      <small>Seleccione una categoría</small>
                    </div>
                  </div>

                  <div className="gj-dropdown-divider"></div>

                  <button
                    type="button"
                    className="gj-dropdown-item"
                    onClick={() => irA("/industrialr")}
                  >
                    <span className="gj-dropdown-icon">
                      <i className="bi bi-snow"></i>
                    </span>

                    <div>
                      <strong>Refrigeración Industrial</strong>
                      <small>Equipos y componentes industriales</small>
                    </div>

                    <i className="bi bi-chevron-right"></i>
                  </button>

                  <button
                    type="button"
                    className="gj-dropdown-item"
                    onClick={() => irA("/nevera")}
                  >
                    <span className="gj-dropdown-icon">
                      <i className="bi bi-house-gear"></i>
                    </span>

                    <div>
                      <strong>Refrigeración Doméstica</strong>
                      <small>Repuestos para refrigeradoras</small>
                    </div>

                    <i className="bi bi-chevron-right"></i>
                  </button>

                  <button
                    type="button"
                    className="gj-dropdown-item"
                    onClick={() => irA("/lavadora")}
                  >
                    <span className="gj-dropdown-icon">
                      <i className="bi bi-arrow-repeat"></i>
                    </span>

                    <div>
                      <strong>Lavadoras</strong>
                      <small>Repuestos y accesorios</small>
                    </div>

                    <i className="bi bi-chevron-right"></i>
                  </button>

                  <button
                    type="button"
                    className="gj-dropdown-item"
                    onClick={() => irA("/aireacondicionado")}
                  >
                    <span className="gj-dropdown-icon">
                      <i className="bi bi-wind"></i>
                    </span>

                    <div>
                      <strong>Aire Acondicionado</strong>
                      <small>Componentes y accesorios</small>
                    </div>

                    <i className="bi bi-chevron-right"></i>
                  </button>

                  <button
                    type="button"
                    className="gj-dropdown-item"
                    onClick={() => irA("/gas")}
                  >
                    <span className="gj-dropdown-icon">
                      <i className="bi bi-fire"></i>
                    </span>

                    <div>
                      <strong>Instalaciones a Gas</strong>
                      <small>Accesorios y componentes</small>
                    </div>

                    <i className="bi bi-chevron-right"></i>
                  </button>

                  <button
                    type="button"
                    className="gj-dropdown-item"
                    onClick={() => irA("/herramienta")}
                  >
                    <span className="gj-dropdown-icon">
                      <i className="bi bi-tools"></i>
                    </span>

                    <div>
                      <strong>Herramientas</strong>
                      <small>Herramientas para técnicos</small>
                    </div>

                    <i className="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>

              {/* ENVÍOS */}
              <button
                type="button"
                className={`gj-nav-link ${
                  rutaActiva("/envios") ? "active" : ""
                }`}
                onClick={() => irA("/envios")}
              >
                <i className="bi bi-truck"></i>
                <span>Envíos</span>
              </button>

              {/* AYUDA */}
              <button
                type="button"
                className={`gj-nav-link ${
                  rutaActiva("/ayuda") ? "active" : ""
                }`}
                onClick={() => irA("/ayuda")}
              >
                <i className="bi bi-question-circle"></i>
                <span>Ayuda</span>
              </button>

              <button
                type="button"
                className={`gj-nav-link ${
                  rutaActiva("/calculadora") ? "active" : ""
                }`}
                onClick={() => irA("/calculadora")}
              >
                <i className="bi bi-calculator"></i>
                <span>Calculadora</span>
              </button>
            </nav>

            {/* =====================================================
                ACCIONES DERECHA
            ===================================================== */}
            <div className="gj-navbar-actions">
              {/* BUSCAR */}
              <button
                type="button"
                className="gj-search-btn gj-navbar-icon-btn"
                onClick={() => {
                  setMostrarModal(true);
                  cerrarMenu();
                }}
                aria-label="Buscar productos"
                title="Buscar productos"
              >
                <i className="bi bi-search"></i>
              </button>

              {/* ACCESO ADMINISTRADOR */}
              <button
                type="button"
                className={`gj-admin-btn gj-navbar-icon-btn ${
                  rutaActiva("/login") || rutaActiva("/admin-productos")
                    ? "active"
                    : ""
                }`}
                onClick={() => {
                  window.open("/login", "_blank", "noopener,noreferrer");
                  cerrarMenu();
                }}
                aria-label="Acceso administrador"
                title="Acceso administrador"
              >
                <i className="bi bi-lock-fill"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* =========================================================
          MODAL BUSCADOR
      ========================================================= */}
      {mostrarModal && (
        <div
          className="gj-search-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              cerrarBuscador();
            }
          }}
        >
          <div className="gj-search-modal">
            {/* HEADER */}
            <div className="gj-search-header">
              <div className="gj-search-header-info">
                <span className="gj-search-header-icon">
                  <i className="bi bi-search"></i>
                </span>

                <div>
                  <h5>Buscar productos</h5>
                  <p>Encuentre rápidamente lo que necesita</p>
                </div>
              </div>

              <button
                type="button"
                className="gj-search-close"
                onClick={cerrarBuscador}
                aria-label="Cerrar"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            {/* BODY */}
            <div className="gj-search-body">
              <div className="gj-search-input-container">
                <i className="bi bi-search"></i>

                <input
                  type="text"
                  placeholder="Escriba el nombre del producto..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onKeyDown={manejarEnter}
                  autoFocus
                />

                {busqueda && (
                  <button
                    type="button"
                    className="gj-search-clear"
                    onClick={() => {
                      setBusqueda("");
                      setResultados([]);
                      setAlerta("");
                    }}
                  >
                    <i className="bi bi-x-circle"></i>
                  </button>
                )}
              </div>

              {alerta && (
                <div className="gj-search-alert">
                  <i className="bi bi-info-circle"></i>
                  <span>{alerta}</span>
                </div>
              )}

              {/* SIN TEXTO */}
              {!busqueda.trim() && (
                <div className="gj-search-empty">
                  <span>
                    <i className="bi bi-box-seam"></i>
                  </span>

                  <h6>¿Qué producto está buscando?</h6>

                  <p>Escriba el nombre del producto para comenzar.</p>
                </div>
              )}

              {/* RESULTADOS */}
              {busqueda.trim() && resultados.length > 0 && (
                <div className="gj-search-results">
                  <div className="gj-search-results-title">
                    <span>Resultados encontrados</span>

                    <small>{resultados.length}</small>
                  </div>

                  {resultados.map((item) => (
                    <button
                      type="button"
                      key={item._id}
                      className="gj-search-product"
                      onClick={() => {
                        navigate(
                          `/resultados?busqueda=${encodeURIComponent(
                            item.nombre,
                          )}&producto=${item._id}`,
                        );

                        cerrarBuscador();
                      }}
                    >
                      <div className="gj-search-product-image">
                        {item.imagen ? (
                          <img
                            src={item.imagen}
                            alt={item.nombre}
                            loading="lazy"
                          />
                        ) : (
                          <i className="bi bi-image"></i>
                        )}
                      </div>

                      <div className="gj-search-product-info">
                        <strong>{item.nombre}</strong>

                        <small>
                          Código: {item.codigoProducto || "Sin código"}
                        </small>
                      </div>

                      <span className="gj-search-product-arrow">
                        <i className="bi bi-chevron-right"></i>
                      </span>
                    </button>
                  ))}

                  <button
                    type="button"
                    className="gj-search-all"
                    onClick={manejarBusqueda}
                  >
                    <span>Ver todos los productos relacionados</span>
                    <i className="bi bi-arrow-right"></i>
                  </button>
                </div>
              )}

              {/* SIN RESULTADOS */}
              {busqueda.trim() && resultados.length === 0 && (
                <div className="gj-search-empty">
                  <span>
                    <i className="bi bi-search"></i>
                  </span>

                  <h6>No encontramos coincidencias</h6>

                  <p>Intente escribir otra palabra o un nombre más corto.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
